export function isEmbedded() {
    if (typeof window === "undefined")
        return false;
    try {
        if (window.parent === window)
            return false;
    }
    catch {
        // Cross-origin access throws; if it throws, we are in an iframe.
        return true;
    }
    const params = new URLSearchParams(window.location.search);
    return params.get("embedded") === "1";
}
export function currentContentId() {
    if (typeof window === "undefined")
        return null;
    const params = new URLSearchParams(window.location.search);
    return params.get("contentId");
}
/**
 * Origin of the host CMS that embedded this editor. Comes from the
 * `host=` URL param the host injects when building the iframe src.
 *
 * SECURITY: every postMessage we send and receive is keyed to this
 * origin. Without it the editor would broadcast saves and AI
 * settings (which carry the user's API key in plaintext) to any page
 * that happens to embed `embuilder.emkit.io?embedded=1`. An attacker
 * who tricked a user into opening their malicious page could
 * exfiltrate the key by listening for `emkit:ai:settings:set`.
 *
 * Returns null when missing or malformed, in which case bridge calls
 * silently no-op rather than leaking to a wildcard origin.
 */
export function hostOrigin() {
    if (typeof window === "undefined")
        return null;
    const raw = new URLSearchParams(window.location.search).get("host");
    if (!raw)
        return null;
    try {
        const parsed = new URL(raw);
        // Only http/https — reject javascript:, data:, file:, etc.
        if (parsed.protocol !== "https:" && parsed.protocol !== "http:")
            return null;
        return parsed.origin;
    }
    catch {
        return null;
    }
}
/**
 * Open a bridge to the host. The editor calls this once at bootstrap,
 * subscribes to load/ack events, and dispatches save/dirty/media.upload
 * calls when the user acts.
 */
export function createHostBridge() {
    const listeners = {
        load: new Set(),
        loadError: new Set(),
        saveAck: new Set(),
        saveError: new Set(),
    };
    const mediaPending = new Map();
    const listPending = new Map();
    // All AI requests share the same map: settings:get, settings:set, plan:get,
    // generate. The dispatched message types disambiguate via the matching
    // reply type ("emkit:ai:settings" / "emkit:ai:plan" / "emkit:ai:generated").
    const aiPending = new Map();
    // Locked at bridge creation. The host injects ?host=https://… into
    // the iframe src; without it the bridge can't address its parent
    // safely, so we no-op every send. The on-the-wire targetOrigin
    // matches this exactly — no wildcards.
    const trustedOrigin = hostOrigin();
    function post(msg) {
        if (!trustedOrigin) {
            console.warn("[emkit/bridge] no host= URL param — message dropped, set host=https://… on the iframe src");
            return;
        }
        window.parent.postMessage(msg, trustedOrigin);
    }
    function onMessage(ev) {
        // Reject messages from any origin we didn't expect. Without the
        // host= param we have no anchor and refuse everything.
        if (!trustedOrigin || ev.origin !== trustedOrigin)
            return;
        const msg = ev.data;
        if (!msg || typeof msg !== "object" || typeof msg.type !== "string")
            return;
        if (!msg.type.startsWith("emkit:"))
            return;
        switch (msg.type) {
            case "emkit:load":
                for (const fn of listeners.load)
                    fn(msg.page, msg.contentId);
                break;
            case "emkit:load:error":
                for (const fn of listeners.loadError)
                    fn(msg.message);
                break;
            case "emkit:save:ack":
                for (const fn of listeners.saveAck)
                    fn(msg.contentId);
                break;
            case "emkit:save:error":
                for (const fn of listeners.saveError)
                    fn(msg.message);
                break;
            case "emkit:media:uploaded": {
                const pending = mediaPending.get(msg.requestId);
                if (pending) {
                    mediaPending.delete(msg.requestId);
                    pending.resolve(msg.file);
                }
                break;
            }
            case "emkit:media:error": {
                const pending = mediaPending.get(msg.requestId);
                if (pending) {
                    mediaPending.delete(msg.requestId);
                    pending.reject(new Error(msg.message));
                }
                break;
            }
            case "emkit:media:listed": {
                const pending = listPending.get(msg.requestId);
                if (pending) {
                    clearTimeout(pending.timer);
                    listPending.delete(msg.requestId);
                    pending.resolve({ files: msg.files, nextCursor: msg.nextCursor });
                }
                break;
            }
            case "emkit:media:list:error": {
                const pending = listPending.get(msg.requestId);
                if (pending) {
                    clearTimeout(pending.timer);
                    listPending.delete(msg.requestId);
                    pending.reject(new Error(msg.message));
                }
                break;
            }
            case "emkit:ai:settings": {
                const pending = aiPending.get(msg.requestId);
                if (pending) {
                    clearTimeout(pending.timer);
                    aiPending.delete(msg.requestId);
                    pending.resolve(msg.settings);
                }
                break;
            }
            case "emkit:ai:plan": {
                const pending = aiPending.get(msg.requestId);
                if (pending) {
                    clearTimeout(pending.timer);
                    aiPending.delete(msg.requestId);
                    pending.resolve(msg.plan);
                }
                break;
            }
            case "emkit:ai:generated": {
                const pending = aiPending.get(msg.requestId);
                if (pending) {
                    clearTimeout(pending.timer);
                    aiPending.delete(msg.requestId);
                    pending.resolve(msg.result);
                }
                break;
            }
            case "emkit:ai:error": {
                const pending = aiPending.get(msg.requestId);
                if (pending) {
                    clearTimeout(pending.timer);
                    aiPending.delete(msg.requestId);
                    pending.reject(new Error(msg.message));
                }
                break;
            }
        }
    }
    function aiRequest(send, timeoutMs) {
        const requestId = `ai-${Math.random().toString(36).slice(2, 10)}`;
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                if (aiPending.delete(requestId)) {
                    reject(new Error("AI request timed out — host did not respond"));
                }
            }, timeoutMs);
            aiPending.set(requestId, {
                resolve: (v) => resolve(v),
                reject,
                timer,
            });
            send(requestId);
        });
    }
    window.addEventListener("message", onMessage);
    post({ type: "emkit:ready" });
    const bridge = {
        onLoad(fn) {
            listeners.load.add(fn);
            return () => listeners.load.delete(fn);
        },
        onLoadError(fn) {
            listeners.loadError.add(fn);
            return () => listeners.loadError.delete(fn);
        },
        onSaveAck(fn) {
            listeners.saveAck.add(fn);
            return () => listeners.saveAck.delete(fn);
        },
        onSaveError(fn) {
            listeners.saveError.add(fn);
            return () => listeners.saveError.delete(fn);
        },
        save(page) {
            post({ type: "emkit:save", page });
        },
        markDirty(dirty) {
            post({ type: "emkit:dirty", dirty });
        },
        async uploadMedia(filename, mimeType, data) {
            const requestId = `m-${Math.random().toString(36).slice(2, 10)}`;
            const dataBase64 = bytesToBase64(data);
            return new Promise((resolve, reject) => {
                mediaPending.set(requestId, { resolve, reject });
                post({
                    type: "emkit:media:upload",
                    requestId,
                    filename,
                    mimeType,
                    dataBase64,
                });
            });
        },
        async listMedia(filter) {
            const requestId = `l-${Math.random().toString(36).slice(2, 10)}`;
            return new Promise((resolve, reject) => {
                // Older hosts ignore emkit:media:list. Treat silence as "empty" after
                // a short grace so the gallery degrades to upload-only instead of
                // hanging forever.
                const timer = setTimeout(() => {
                    if (listPending.delete(requestId)) {
                        resolve({ files: [] });
                    }
                }, 2000);
                listPending.set(requestId, { resolve, reject, timer });
                post({ type: "emkit:media:list", requestId, filter });
            });
        },
        getAISettings() {
            return aiRequest((requestId) => post({ type: "emkit:ai:settings:get", requestId }), 4000);
        },
        setAISettings(input) {
            return aiRequest((requestId) => post({
                type: "emkit:ai:settings:set",
                requestId,
                provider: input.provider,
                model: input.model,
                apiKey: input.apiKey,
            }), 6000);
        },
        getPlan() {
            return aiRequest((requestId) => post({ type: "emkit:ai:plan:get", requestId }), 4000);
        },
        generateWithAI(payload) {
            // LLMs can take a while; keep the timeout generous but bounded.
            return aiRequest((requestId) => post({ type: "emkit:ai:generate", requestId, payload }), 120_000);
        },
        destroy() {
            window.removeEventListener("message", onMessage);
            listeners.load.clear();
            listeners.saveAck.clear();
            listeners.saveError.clear();
            listeners.loadError.clear();
            mediaPending.clear();
            for (const p of listPending.values())
                clearTimeout(p.timer);
            listPending.clear();
            for (const p of aiPending.values())
                clearTimeout(p.timer);
            aiPending.clear();
            if (singleton === bridge)
                singleton = null;
        },
    };
    singleton = bridge;
    return bridge;
}
/**
 * Module-scoped singleton. One bridge per iframe, regardless of how
 * many components call `getHostBridge()`. Tests that need a fresh
 * instance should call `createHostBridge()` directly and destroy().
 */
let singleton = null;
export function getHostBridge() {
    if (!singleton)
        singleton = createHostBridge();
    return singleton;
}
function bytesToBase64(data) {
    const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
    let binary = "";
    // Chunked conversion avoids blowing the call stack on large files.
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
        const slice = bytes.subarray(i, i + chunk);
        binary += String.fromCharCode(...slice);
    }
    return btoa(binary);
}
//# sourceMappingURL=bridge.js.map